import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './Sidebar';
import type { Page, StaffMember, CurrentUser, RegisterSession } from '../../types';
import { useFullscreen } from '../../hooks/useFullscreen';
import FullscreenIcon from '../icons/FullscreenIcon';
import ExitFullscreenIcon from '../icons/ExitFullscreenIcon';
import SharedRegisterBanner from '../pos/SharedRegisterBanner';
import { config } from '../../lib/config';

const MOBILE_BREAKPOINT = 768;

interface MainLayoutProps {
  children: React.ReactNode;
  activePage: Page;
  setActivePage: (page: Page) => void;
  currentUser: CurrentUser;
  onLogout: () => void;
  onLockRegister?: () => void;
  isRegisterLocked?: boolean;
  onRequestPinEntry?: () => void;
  isPinBasedAccess?: boolean;
  /** Active shared register session, for the always-visible top-bar chip. */
  registerSession?: RegisterSession | null;
  /** Hide sidebar entirely (waitstaff mobile order mode). */
  hideSidebar?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, activePage, setActivePage, currentUser, onLogout, onLockRegister, isRegisterLocked, onRequestPinEntry, isPinBasedAccess, registerSession, hideSidebar }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT);
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const hideSidebarInMobilePos = hideSidebar || (activePage === 'pos-mobile' && isFullscreen);

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const pagesToCollapse = [
      'pos',
      'pos-mobile',
      'kds',
      'service',
      'reports',
      'reports-sales-insights',
      'reports-event-stall',
      'reports-monthly',
      'reports-financial',
      'reports-reconciliation',
      'reports-food-cost',
    ];
    if (isMobileViewport && activePage === 'expenses') {
      setIsSidebarCollapsed(true);
    } else {
      setIsSidebarCollapsed(pagesToCollapse.includes(activePage));
    }
  }, [activePage, isMobileViewport]);

  const standbyHostLabel = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const host = window.location.hostname.toLowerCase();
    if (host === 'pos2.kingspark.co.nz') {
      return `Standby URL (pos2) — v${config.appVersion}`;
    }
    return null;
  }, []);

  return (
    <div className="h-screen flex font-sans bg-gray-100">
      {!hideSidebarInMobilePos && (
        <Sidebar 
          activePage={activePage} 
          setActivePage={setActivePage} 
          currentUser={currentUser} 
          onLogout={onLogout}
          isCollapsed={isSidebarCollapsed}
          setCollapsed={setIsSidebarCollapsed}
          onLockRegister={onLockRegister}
          isRegisterLocked={isRegisterLocked}
          onRequestPinEntry={onRequestPinEntry}
          isPinBasedAccess={isPinBasedAccess}
        />
      )}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {standbyHostLabel && (
          <div
            className="shrink-0 bg-amber-500 text-amber-950 text-center text-sm font-medium py-1.5 px-3 z-40"
            role="status"
          >
            {standbyHostLabel} — same live data as main POS. Use for in-venue POS/KDS only during failover.
          </div>
        )}
        {/* Live-data badge and shared-register chip used to live up here as
            fixed-positioned widgets in the top-right corner. They overlapped
            page content (POS order summary, KDS action bar) and each other
            on narrow viewports. They now live inline:
              - LiveDataStatusBadge → Sidebar footer (always visible while the
                sidebar is rendered, no overlap with page content).
              - SharedRegisterChip   → POSHeader, next to the close-register
                control. */}
        {/* Fullscreen Toggle – hidden on Mobile POS (already fullscreen on device) */}
        {activePage !== 'pos-mobile' && (
          <button
            onClick={toggleFullscreen}
            className="fixed top-4 right-4 z-50 p-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-lg"
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            {isFullscreen ? (
              <ExitFullscreenIcon className="w-5 h-5" />
            ) : (
              <FullscreenIcon className="w-5 h-5" />
            )}
          </button>
        )}
        {/* One-time "you're sharing" banner. Only relevant on order-taking
            screens, so we gate by activePage to avoid noise on Reports etc.
            The component itself bails out unless the device is adopting
            someone else's session and hasn't dismissed for this sessionId. */}
        {(activePage === 'pos' || activePage === 'pos-mobile' || activePage === 'kds') && (
          <SharedRegisterBanner session={registerSession} currentUser={currentUser} />
        )}
        {children}
      </div>
    </div>
  );
};

export default MainLayout;
